import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "law-firm-google-maps-colorado",
  metaTitle: "Law Firm Google Maps Optimization in Colorado | GBP for Attorneys",
  metaDescription:
    "Rank in the Google Map Pack for legal services across Colorado — practice-area GBP optimization, review velocity, and consultation flow. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Law firm SEO", to: "/law-firm-seo-colorado" },
  ],
  currentLabel: "Law Firm Google Maps Colorado",
  eyebrow: "Law Firm Google Maps — Colorado",
  h1: (
    <>
      Law Firm <span className="text-brand-gold">Google Maps</span> in Colorado
    </>
  ),
  heroDescription:
    "Win the Map Pack for your practice area in your Colorado market — because that's where qualified consultations start.",
  intro:
    "Legal Map Pack ranking in Colorado is brutally competitive — and brutally rewarding. We build out your Google Business Profile with proper practice-area categories, weekly Posts, attorney photos, Q&A monitoring, and a review-velocity system tied to case completion. Personal injury, family law, criminal defense, estate planning — each practice area gets the GBP signals it needs to outrank Avvo and FindLaw locally.",
  definition: {
    term: "Law firm Google Maps optimization in Colorado",
    body: "Law firm Google Maps optimization is the process of ranking your firm in the Map Pack for high-intent searches like 'personal injury lawyer Denver', 'family law attorney Colorado Springs', or 'criminal defense near me' — through GBP optimization, review velocity, and practice-area tuning.",
  },
  whatWeDo: [
    { title: "Full GBP rebuild for legal", body: "Primary practice-area category, secondary categories, all relevant practice services, attributes, attorney bios — fully optimized." },
    { title: "Weekly GBP Posts", body: "Case results (within ethics rules), practice-area highlights, attorney spotlights, community involvement — published weekly." },
    { title: "Professional photos", body: "Office interiors, attorney headshots, courtroom imagery, community involvement — all geo-tagged and uploaded systematically." },
    { title: "Review velocity tied to case milestones", body: "Ethics-compliant review requests sent at the right moment in the case lifecycle — not aggressive batch-blasting that triggers Google filtering." },
    { title: "Q&A monitoring", body: "Seed common legal questions for your practice areas, monitor for new ones, respond promptly within bar advertising rules." },
    { title: "Service-area tuning", body: "Adjust service-area boundaries based on actual ranking and intake data — by practice area." },
  ],
  whyThisMarket: [
    {
      heading: "Legal Map Pack is the most competitive vertical in Colorado",
      body: "PI lawyer terms in Denver and Colorado Springs are some of the most expensive in the country. Map Pack ranking is the single highest-leverage place to compete because it's free and compounds.",
    },
    {
      heading: "Review velocity beats review count",
      body: "Most established Colorado firms have plenty of reviews — but stale ones. Firms with consistent, recent review velocity routinely outrank firms with 5x the review count from years ago.",
    },
    {
      heading: "Avvo and FindLaw dominate organic — but not Maps",
      body: "Lead-gen aggregators control the organic search results, but Google Maps weights local presence and review signals they can't replicate. Maps is where Colorado firms can actually win.",
    },
  ],
  relatedLinks: [
    { to: "/law-firm-seo-colorado", label: "Law firm SEO in Colorado — full hub" },
    { to: "/colorado-springs-google-maps", label: "Local SEO overview" },
    { to: "/law-firm-ppc-colorado", label: "Law firm PPC in Colorado" },
    { to: "/colorado-springs-google-maps", label: "Colorado Springs Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado law firm case studies" },
    { to: "/google-maps", label: "Google Maps optimization (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How fast can a Colorado law firm rank in the Map Pack?", a: "Most Colorado firms see meaningful Map Pack movement in 60–120 days. Legal is competitive, so consistent monthly optimization and review velocity is what compounds. PI in Denver is the slowest market; smaller cities and niche practice areas move faster." },
    { q: "Are review-request workflows ethics-compliant in Colorado?", a: "Yes — when done right. We build review-request flows that comply with Colorado bar advertising rules: no incentivized reviews, no fake reviews, no ghost-writing. Just systematic, timely outreach to satisfied clients at appropriate case milestones." },
    { q: "Should I list every practice area as a GBP service?", a: "List every practice area you actively take cases in. Google ranks each independently, and an under-listed GBP forfeits ranking opportunity. Don't list practice areas you don't actually take — that creates intake friction and weak reviews." },
    { q: "Do you only serve Colorado Springs law firms?", a: "No — we serve law firms across all of Colorado: Denver, Boulder, Fort Collins, Pueblo, Castle Rock, and the Front Range. Colorado-only by design." },
  ],
  serviceName: "Law Firm Google Maps Optimization in Colorado",
  serviceDescription:
    "Google Business Profile optimization and Map Pack ranking for law firms across Colorado, by practice area.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/law-firm-google-maps-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
